import React, { useRef } from "react";
import { fromJS } from "immutable";
import { Box, Button, Flyout, SelectOption } from "@showitinc/showit-wc";

import Input from "../../base/Input";
import AppConstants from "../../../constants/AppConstants";
import { INPUT_TYPE_NUMBER } from "../../../constants/ElementConstants";
import {
  SHADOW,
  SHADOW_BLUR,
  SHADOW_COLOR,
  SHADOW_OFFSET_X,
  SHADOW_OFFSET_Y,
  SHADOW_STYLE,
} from "../../../constants/MergeConstants";
import SplitMergeWrapper from "../../base/SplitMergeWrapper";
import { initFormatter } from "../../../utils/PropertyFormatters";
import { COLOR_PICKER, SELECT } from "../../../constants/InputConstants.mjs";
import { isValueMixed } from "../../../utils/InheritanceUtils";
import { MIXED } from "../../../constants/DesignConstants.mjs";

/**
 * @typedef {import("immutable").Map} ImmutableMap
 */

const { DEFAULT_DROP_SHADOW_PROPERTIES } = AppConstants;

const SHADOW_XY_MAX = 100;

const SHADOW_STYLE_INNER = "inner";
const SHADOW_STYLE_NONE = "none";
const SHADOW_STYLE_OUTER = "outer";

/**
 *
 * @param {{
 *  disableInsetShadow?: boolean,
 * getPropName: function,
 * isLocked: boolean,
 * layout: string,
 * mixedSync: boolean,
 * onPropUpdate: function,
 * shadowProps: ImmutableMap,
 * sync: boolean,
 * toggleSync: function,
 * removeCustomization?: function
 * }} props
 * @returns
 */
const DropShadowProperties = ({
  disableInsetShadow = false,
  isLocked,
  layout,
  mixedSync,
  onPropUpdate,
  getPropName,
  shadowProps,
  sync,
  toggleSync,
  removeCustomization,
}) => {
  const flyoutRef = useRef();
  const inputRef = useRef();

  const dropShadowProps = fromJS({
    shadow: { ...DEFAULT_DROP_SHADOW_PROPERTIES },
  }).mergeDeep(isValueMixed(shadowProps) ? {} : { shadow: shadowProps });

  const _typeChange = (propInfo) => {
    const { value } = propInfo;
    onPropUpdate(propInfo);
    if ([SHADOW_STYLE_INNER, SHADOW_STYLE_OUTER].includes(value)) {
      flyoutRef.current.showPopover(inputRef.current);
    }
  };

  const separatePath = (path) => path.split(".");
  const getValue = (path) => dropShadowProps.getIn(separatePath(path));
  const prepChange = (path) => [path, getValue(path)];
  const { onChange: onTypeChange } = initFormatter(_typeChange, getPropName);
  const { onChange } = initFormatter(onPropUpdate, (propName, __) =>
    getPropName(propName, SHADOW_STYLE)
  );

  const shadowStyle = isValueMixed(shadowProps)
    ? MIXED
    : getValue(SHADOW_STYLE) ?? SHADOW_STYLE_NONE;

  return (
    <>
      <SplitMergeWrapper
        toggleSync={toggleSync}
        sync={sync}
        layout={layout}
        mixed={mixedSync}
        removeCustomization={removeCustomization}
      >
        <Input
          data-testid="drop-shadow-select"
          context={!isValueMixed(shadowStyle) && !mixedSync}
          disabled={isLocked}
          inputType={SELECT}
          label="Drop Shadow"
          onSISelect={onTypeChange(...prepChange(SHADOW_STYLE))}
          ref={inputRef}
          shift="left"
          value={shadowStyle || SHADOW_STYLE_NONE}
        >
          <SelectOption
            data-testid="drop-shadow-none-option"
            value={SHADOW_STYLE_NONE}
          >
            None
          </SelectOption>
          <SelectOption
            data-testid="drop-shadow-outer-option"
            value={SHADOW_STYLE_OUTER}
          >
            Outer
          </SelectOption>
          {!disableInsetShadow && (
            <SelectOption
              data-testid="drop-shadow-inner-"
              value={SHADOW_STYLE_INNER}
            >
              Inner
            </SelectOption>
          )}
          {![SHADOW_STYLE_NONE, undefined, MIXED].includes(shadowStyle) && (
            <Button
              data-testid="drop-shadow-flyout-button"
              slot="suffix"
              variant="icon"
              iconPrefix="mix"
              onSIClick={() => flyoutRef.current.showPopover(inputRef.current)}
            />
          )}
        </Input>
      </SplitMergeWrapper>
      <Flyout ref={flyoutRef} title="Drop Shadow" position="top" flip="false">
        <Box pb="2">
          <Input
            data-testid="drop-shadow-color-picker"
            color={getValue(SHADOW_COLOR)}
            disabled={isLocked}
            inputType={COLOR_PICKER}
            label="Color"
            reserveContext
            onSIChange={onChange(...prepChange(SHADOW_COLOR))}
            onSIBeforeChange={onChange(...prepChange(SHADOW_COLOR))}
            onSIInput={onChange(...prepChange(SHADOW_COLOR), false)}
          />
          <Input
            data-testid="drop-shadow-x-offset-input"
            disabled={isLocked}
            label="X-Offset"
            min={-SHADOW_XY_MAX}
            max={SHADOW_XY_MAX}
            onSIBeforeChange={onChange(...prepChange(SHADOW_OFFSET_X))}
            onSIInput={onChange(...prepChange(SHADOW_OFFSET_X), false)}
            type={INPUT_TYPE_NUMBER}
            units="px"
            value={getValue(SHADOW_OFFSET_X)}
          />
          <Input
            data-testid="drop-shadow-y-offset-input"
            disabled={isLocked}
            label="Y-Offset"
            min={-SHADOW_XY_MAX}
            max={SHADOW_XY_MAX}
            onSIBeforeChange={onChange(...prepChange(SHADOW_OFFSET_Y))}
            onSIInput={onChange(...prepChange(SHADOW_OFFSET_Y), false)}
            type={INPUT_TYPE_NUMBER}
            units="px"
            value={getValue(SHADOW_OFFSET_Y)}
          />
          <Input
            data-testid="drop-shadow-blur-input"
            disabled={isLocked}
            label="Blur"
            min={0}
            onSIBeforeChange={onChange(...prepChange(SHADOW_BLUR))}
            onSIInput={onChange(...prepChange(SHADOW_BLUR), false)}
            type={INPUT_TYPE_NUMBER}
            units="px"
            value={getValue(SHADOW_BLUR)}
          />
        </Box>
      </Flyout>
    </>
  );
};

export default DropShadowProperties;
